import type { ValueGetterParams } from 'ag-grid-community';
import type { ForwardDealInfo, VanillaOptionDealInfo } from '../../../../types/OTC/otcDealServerTypes';
import { isOTCForwardSecurity, isOTCOptionSecurity } from '../../../../types/OTC/otcSecurityTypeGuards';
import type { Security } from '../../../../types/Security';
import { SubProductTypeEnum } from '../../../../types/types';
import type { BlotterTableContextProps } from '../../BlotterTableContext';
import {
  getOtcStrikeFwdPriceValueFromForwardDealInfo,
  getOtcStrikeFwdPriceValueFromVanillaOptionDealInfo,
  type OtcStrikeFwdPriceValue,
} from './otcStrikeFwdPriceValue';

/** Minimal row shape for {@link otcStrikeFwdPrice} (OTC forwards and vanilla options). */
export type OtcStrikeFwdPriceRow =
  | { Symbol: string; SubProductType: typeof SubProductTypeEnum.OTCForward; DealInfo: ForwardDealInfo }
  | { Symbol: string; SubProductType: typeof SubProductTypeEnum.OTCVanillaOption; DealInfo: VanillaOptionDealInfo };

function getBlotterContextProps(context: unknown): BlotterTableContextProps | undefined {
  if (context && typeof context === 'object' && 'current' in context) {
    return (context as { current?: BlotterTableContextProps }).current;
  }
  return undefined;
}

/** Value for {@link otcStrikeFwdPrice} column on OTC deal blotter rows (strike: deal/template StrikePriceCcy, else USD). */
export function getOtcStrikeFwdPriceColumnValue(
  params: ValueGetterParams<OtcStrikeFwdPriceRow>
): OtcStrikeFwdPriceValue | undefined {
  const data = params.data;
  if (!data) {
    return undefined;
  }

  const blotterCtx = getBlotterContextProps(params.context);
  const security: Security | undefined = blotterCtx?.securitiesBySymbol?.get(data.Symbol);

  switch (data.SubProductType) {
    case SubProductTypeEnum.OTCForward:
      return getOtcStrikeFwdPriceValueFromForwardDealInfo(
        data.DealInfo,
        isOTCForwardSecurity(security) ? security.ProductDetails.QuoteAsset : undefined
      );
    case SubProductTypeEnum.OTCVanillaOption: {
      if (isOTCOptionSecurity(security)) {
        const pd = security.ProductDetails;
        return getOtcStrikeFwdPriceValueFromVanillaOptionDealInfo(data.DealInfo, pd.StrikePriceCcy);
      }

      return getOtcStrikeFwdPriceValueFromVanillaOptionDealInfo(data.DealInfo);
    }
    default:
      return undefined;
  }
}
